import { RELICS } from './relics';
import { BOSS_MAX_HP } from './corePhases';
import { MINI_BOSS_HP } from './sectorEnemies';
import { CORE_SECTOR } from './runConstants';
import { getIceTileIndices } from './boardConditions';
import { getBoardEffectTotal, getTriggeredBoardSquares } from './chainReaction';
import { getThrowCombo, isLinkedThrow } from './throwCombo';
import { isNegativeBoardSquare } from './doorBoard';

const getRelicAttackNumber = (game) =>
  game.guardian?.active
    ? game.guardian.round
    : game.miniBoss?.active
    ? game.miniBoss.round
    : game.floor === CORE_SECTOR
    ? game.bossRound
    : 1;

const startingHealth = (game) => {
  const guardian = game.guardian;
  const encounter = guardian?.active
    ? guardian
    : game.miniBoss?.active
    ? game.miniBoss
    : null;
  const lastRound = encounter?.lastRound;
  if (
    lastRound?.round === encounter?.round &&
    Number.isFinite(lastRound?.playerHpBefore)
  ) {
    const hp = lastRound.playerHpBefore;
    return {
      hp,
      maxHp: lastRound.playerMaxHpBefore ?? Math.max(10, game.maxHp || 0, hp),
    };
  }
  const round =
    game.floor === CORE_SECTOR && game.bossRoundState?.round === game.bossRound
      ? game.bossRoundState
      : null;
  const hp = round?.hpBefore ?? game.hp;
  return { hp, maxHp: round?.maxHpBefore ?? Math.max(10, game.maxHp || 0, hp) };
};

const getRelicEnemyHealth = (game) => {
  if (game.guardian?.active) {
    const guardian = game.guardian;
    const round =
      guardian.lastRound?.round === guardian.round ? guardian.lastRound : null;
    return { hp: round?.enemyHpBefore ?? guardian.hp, maxHp: guardian.maxHp };
  }
  if (game.floor === CORE_SECTOR) {
    const round =
      game.bossRoundState?.round === game.bossRound
        ? game.bossRoundState
        : null;
    return { hp: round?.bossHpBefore ?? game.bossHp, maxHp: BOSS_MAX_HP };
  }
  if (game.miniBoss?.active)
    return { hp: game.miniBoss.hp, maxHp: MINI_BOSS_HP };
  return null;
};

export const relicConditionMatches = (condition, game, options = {}) => {
  const roll = options.adjustedRoll ?? game.roll;
  if (!Number.isFinite(roll)) return false;
  const digits = String(roll).padStart(2, '0');
  const events = getThrowCombo(game)?.events || [];
  const health = startingHealth(game);
  switch (condition) {
    case 'always':
      return true;
    case 'odd':
      return roll % 2 === 1;
    case 'even':
      return roll % 2 === 0;
    case 'low':
      return roll <= 10;
    case 'low20':
      return roll <= 20;
    case 'low25':
      return roll <= 25;
    case 'low35':
      return roll <= 35;
    case 'low50':
      return roll <= 50;
    case 'high':
      return roll >= (options.minRoll ?? 75);
    case 'high40':
      return roll >= (options.minRoll ?? 40);
    case 'very-high':
      return roll >= (options.minRoll ?? 80);
    case 'contains7':
      return digits.includes('7');
    case 'double':
      return roll >= 0 && roll < 100 && digits[0] === digits[1];
    case 'different':
      return roll >= 0 && roll < 100 && digits[0] !== digits[1];
    case 'multiple5':
      return roll % 5 === 0;
    case 'multiple3':
      return roll % 3 === 0;
    case 'multiple10':
      return roll % 10 === 0;
    case 'heal-tile':
      return getBoardEffectTotal(game, 'heal') > 0;
    case 'shard-tile':
      return getBoardEffectTotal(game, 'shards') > 0;
    case 'shield-tile':
      return getBoardEffectTotal(game, 'shield') > 0;
    case 'critical-tile':
      return getTriggeredBoardSquares(game).some(
        ({ square }) => square.effect?.type === 'critical'
      );
    case 'positive-tile':
      return getTriggeredBoardSquares(game).some(
        ({ square }) => square.modifier > 0
      );
    case 'negative-tile':
      return getTriggeredBoardSquares(game).some(({ square }) =>
        isNegativeBoardSquare(square)
      );
    case 'three-tiles':
      return getTriggeredBoardSquares(game).length >= 3;
    case 'portal':
      return events.some(({ type }) => type === 'portal');
    case 'anchored':
      return events.some(({ type }) => type === 'anchor');
    case 'bumped':
      return events.some(({ type }) => type === 'bump');
    case 'two-bumps':
      return events.filter(({ type }) => type === 'bump').length >= 2;
    case 'no-bumps':
      return !events.some(({ type }) => type === 'bump');
    case 'linked-throw':
      return isLinkedThrow(getThrowCombo(game));
    case 'portal-bump':
      return (
        events.some(({ type }) => type === 'portal') &&
        events.some(({ type }) => type === 'bump')
      );
    case 'powered':
      return (
        (game.powerUp?.boosts || 0) > 0 ||
        getTriggeredBoardSquares(game).some((entry) => entry.powerUpMultiplier)
      );
    case 'full-power':
      return (game.slingshotLaunch?.power || 0) >= 0.85;
    case 'soft-launch':
      return (
        Number.isFinite(game.slingshotLaunch?.power) &&
        game.slingshotLaunch.power <= 0.5
      );
    case 'measured-launch':
      return (
        Number.isFinite(game.slingshotLaunch?.power) &&
        game.slingshotLaunch.power > 0.5 &&
        game.slingshotLaunch.power < 0.85
      );
    case 'corner-tile':
      return [0, 4, 20, 24].includes(game.landingIndex);
    case 'clean-center':
      return (
        game.landingIndex === 12 && !events.some(({ type }) => type === 'bump')
      );
    case 'relay-routed':
      return getTriggeredBoardSquares(game).some(
        ({ path }) => path?.length > 1
      );
    case 'anchor-bump':
      return (
        events.some(({ type }) => type === 'anchor') &&
        events.some(({ type }) => type === 'bump')
      );
    case 'first-attack':
      return getRelicAttackNumber(game) === 1;
    case 'follow-up':
      return getRelicAttackNumber(game) > 1;
    case 'wounded-enemy': {
      const enemy = getRelicEnemyHealth(game);
      return !!enemy && enemy.hp > 0 && enemy.hp <= enemy.maxHp / 2;
    }
    case 'half-hp':
      return health.hp <= health.maxHp / 2;
    case 'low-roll-low-hp':
      return roll <= 35 && health.hp <= health.maxHp / 2;
    case 'ascending-digits':
      return (
        roll >= 10 && roll < 100 && roll % 10 === Math.floor(roll / 10) + 1
      );
    case 'descending-digits':
      return (
        roll >= 10 && roll < 100 && Math.floor(roll / 10) === (roll % 10) + 1
      );
    case 'ends-one':
      return roll >= 0 && roll % 10 === 1;
    case 'low-hp':
      return health.hp <= 4;
    case 'five-hp':
      return health.hp <= 5;
    case 'natural72':
      return game.roll >= 72;
    case 'mirrored':
      return !!game.flipped;
    case 'nonpositive':
      return roll <= 0;
    case 'center-row':
      return game.landingIndex >= 10 && game.landingIndex <= 14;
    case 'edge-tile': {
      const index = game.landingIndex;
      return (
        Number.isInteger(index) &&
        index >= 0 &&
        index < 25 &&
        (index < 5 || index >= 20 || index % 5 === 0 || index % 5 === 4)
      );
    }
    case 'force-field':
      return ['crosswind', 'magnetic'].includes(game.boardCondition?.type);
    case 'ice-landing':
      return getIceTileIndices(game.boardCondition).includes(game.landingIndex);
    case 'low-gravity-bumps':
      return (
        game.boardCondition?.type === 'lowGravity' &&
        events.filter(({ type }) => type === 'bump').length >= 2
      );
    case 'overkill':
      return (
        options.attack - options.enemyHpBefore >= (options.overkillMargin ?? 25)
      );
    case 'overkill-50':
      return options.attack - options.enemyHpBefore > 50;
    default:
      return false;
  }
};

export const getRelicHealingEffects = (
  game,
  { victory = false, includeAttack = true, ...options } = {}
) =>
  (game.relics || []).flatMap((id) => {
    const relic = RELICS[id];
    if (!relic) return [];
    if (relic.healOncePerRun && game.relicHealingUses?.[id]) return [];
    const amount =
      (includeAttack &&
      relic.healAmount &&
      relicConditionMatches(relic.healCondition || 'always', game, options)
        ? relic.healAmount
        : 0) +
      (victory &&
      relicConditionMatches(relic.successCondition || 'always', game, options)
        ? relic.successHeal || 0
        : 0);
    return amount > 0
      ? [{ id, amount, message: `${relic.name}: + ${amount} Max HP` }]
      : [];
  });

// Spend a limited heal only when health is committed, never during previews.
export const consumeRelicHealingEffects = (game, effects) => {
  const spent = effects.filter(({ id }) => RELICS[id]?.healOncePerRun);
  if (!spent.length) return game;
  return {
    ...game,
    relicHealingUses: {
      ...game.relicHealingUses,
      ...Object.fromEntries(spent.map(({ id }) => [id, true])),
    },
  };
};

export const getRelicBlockEffects = (game, options = {}) =>
  (game.relics || []).flatMap((id) => {
    const relic = RELICS[id];
    return relic?.damageReduction &&
      relicConditionMatches(relic.blockCondition || 'always', game, options)
      ? [{ id, amount: relic.damageReduction }]
      : [];
  });

export const getRelicShardEffects = (
  game,
  { victory = false, ...options } = {}
) =>
  !victory
    ? []
    : (game.relics || []).flatMap((id) => {
        const relic = RELICS[id];
        if (
          !relic?.successShards ||
          (relic.healOncePerRun && game.relicHealingUses?.[id]) ||
          !relicConditionMatches(
            relic.successCondition || 'always',
            game,
            options
          ) ||
          !relicConditionMatches(
            relic.shardCondition ||
              (relic.highRollShards ? 'very-high' : 'always'),
            game,
            { ...options, minRoll: relic.highRollShards ? 90 : relic.minRoll }
          )
        )
          return [];
        return [
          {
            id,
            amount: relic.successShards,
            message: `${relic.name}: +${relic.successShards} shards`,
          },
        ];
      });
